import { Link } from 'react-router';
import {
  LIBELLE_PRIORITE,
  LIBELLE_STATUT_COURRIER,
  LIBELLE_STATUT_FACTURE,
  LIBELLE_STATUT_INSTRUCTION,
  LIBELLE_TYPE_INSTRUCTION,
  type EvenementHistorique,
  type InstructionResume,
  type Priorite,
  type StatutCourrier,
  type StatutFacture,
  type StatutInstruction,
} from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, dateHeureFr } from '@/lib/utils';
import { Vide } from './common';

const COULEUR_PRIORITE: Record<Priorite, string> = { NORMALE: 'bg-muted text-muted-foreground', URGENTE: 'bg-amber-100 text-amber-900', TRES_URGENTE: 'bg-red-100 text-red-800' };
export const PrioriteBadge = ({ priorite }: { priorite: Priorite }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_PRIORITE[priorite])}>
    {LIBELLE_PRIORITE[priorite]}
  </Badge>
);

const COULEUR_COURRIER: Record<StatutCourrier, string> = {
  ENREGISTRE: 'bg-muted text-muted-foreground',
  ANALYSE: 'bg-violet-100 text-violet-800',
  SOUMIS: 'bg-blue-100 text-blue-800',
  ANNOTE: 'bg-blue-100 text-blue-800',
  TRANSMIS: 'bg-sky-100 text-sky-800',
  EN_COURS: 'bg-amber-100 text-amber-900',
  REPONSE_RECUE: 'bg-emerald-100 text-emerald-800',
  CLOTURE: 'bg-neutral-200 text-neutral-700',
  ANNULE: 'bg-neutral-200 text-neutral-700',
};
export const StatutCourrierBadge = ({ statut }: { statut: StatutCourrier }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_COURRIER[statut])}>
    {LIBELLE_STATUT_COURRIER[statut]}
  </Badge>
);

const COULEUR_FACTURE: Partial<Record<StatutFacture, string>> = {
  SOUMISE: 'bg-blue-100 text-blue-800',
  ORIENTEE: 'bg-sky-100 text-sky-800',
  SUSPENDUE: 'bg-red-100 text-red-800',
  RETOURNEE: 'bg-amber-100 text-amber-900',
  TRANSMISE_CPM: 'bg-violet-100 text-violet-800',
  FED_EMIS: 'bg-violet-100 text-violet-800',
  MISE_EN_PAIEMENT: 'bg-amber-100 text-amber-900',
  PAYEE: 'bg-emerald-100 text-emerald-800',
  CLOTUREE: 'bg-neutral-200 text-neutral-700',
};
export const StatutFactureBadge = ({ statut }: { statut: StatutFacture }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_FACTURE[statut] ?? 'bg-muted text-muted-foreground')}>
    {LIBELLE_STATUT_FACTURE[statut]}
  </Badge>
);

const COULEUR_INSTRUCTION: Record<StatutInstruction, string> = {
  NOUVELLE: 'bg-blue-100 text-blue-800',
  CONSULTEE: 'bg-sky-100 text-sky-800',
  EN_COURS: 'bg-amber-100 text-amber-900',
  REPONSE: 'bg-emerald-100 text-emerald-800',
  COMPLEMENT: 'bg-orange-100 text-orange-900',
  CLOTUREE: 'bg-neutral-200 text-neutral-700',
};
export const StatutInstructionBadge = ({ statut }: { statut: StatutInstruction }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_INSTRUCTION[statut])}>
    {LIBELLE_STATUT_INSTRUCTION[statut]}
  </Badge>
);

export function HistoriqueListe({ historique }: { historique: EvenementHistorique[] }) {
  if (!historique.length) return null;
  return (
    <ul className="divide-y text-sm">
      {[...historique].reverse().map((e, i) => (
        <li key={i} className="py-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">{e.action}</span>
            <span className="text-xs text-muted-foreground">{dateHeureFr(e.date)}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {e.auteurNom}
            {e.titulaireId ? ' · par délégation' : ''}
          </div>
          {e.detail && <div className="mt-1 rounded bg-muted px-2 py-1 text-sm">{e.detail}</div>}
        </li>
      ))}
    </ul>
  );
}

/** Tableau d'instructions (registre, page d'un courrier ou d'une facture, parapheur). */
export function InstructionsTable({ instructions, document = true, vide = 'Aucune instruction.' }: { instructions: InstructionResume[]; document?: boolean; vide?: string }) {
  if (!instructions.length) return <Vide message={vide} />;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Référence</TableHead>
          <TableHead>Instruction</TableHead>
          <TableHead className="hidden md:table-cell">Responsable</TableHead>
          <TableHead className="hidden sm:table-cell">Échéance</TableHead>
          <TableHead>Statut</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {instructions.map((i) => (
          <TableRow key={i.id}>
            <TableCell>
              <Link to={`/instructions/${i.id}`} className="font-medium text-primary hover:underline">
                {i.reference}
              </Link>
              {document && i.document && (
                <div className="text-xs text-muted-foreground">
                  <Link to={`${i.document.type === 'CE' ? '/courrier/' : '/factures/'}${i.document.id}`} className="hover:underline">
                    {i.document.numero}
                  </Link>
                </div>
              )}
            </TableCell>
            <TableCell className="max-w-[40ch]">
              <div className="truncate">
                <span className="text-xs text-muted-foreground">{LIBELLE_TYPE_INSTRUCTION[i.type]} · </span>
                {i.texte}
              </div>
              {i.demandeReaffectation && <div className="text-xs text-amber-700">Réaffectation demandée</div>}
              {i.reaffectations > 0 && <div className="text-xs text-muted-foreground">{i.reaffectations} réaffectation(s)</div>}
            </TableCell>
            <TableCell className="hidden md:table-cell">
              {i.responsable?.nom}
              <div className="text-xs text-muted-foreground">{i.structureCode}</div>
            </TableCell>
            <TableCell className={cn('hidden sm:table-cell', i.enRetard && 'font-semibold text-red-700', i.rappel && 'text-amber-700')}>
              {dateFr(i.echeance)}
              {i.enRetard && <div className="text-xs">{i.joursRetard} j de retard</div>}
              {i.rappel && <div className="text-xs">à échéance</div>}
            </TableCell>
            <TableCell className="space-x-1">
              <StatutInstructionBadge statut={i.statut} />
              {i.priorite !== 'NORMALE' && <PrioriteBadge priorite={i.priorite} />}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
